import { IconButton, type PopoverProps, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwimlaneEventPublisher } from '@/components/ui/swimlane/api';
import { InternalContext } from '@/components/ui/swimlane/context/InternalContext';
import { StyledContextMenu } from '@/components/ui/swimlane/context-menu';
import { Default } from './options/Default';
import type { IHeaderMenuProps } from './types';

export function SwimlaneHeaderMenu({
  Icon,
  onOpenCard,
  hideHeader,
  openCardLabel,
  hideHeaderTooltip,
  reorderDisabledTooltip,
  isReOrderingDisabled = false,
  isViewCardOptionDisabled,
  level,
  isGroupHeader,
  parentHeaderId,
}: IHeaderMenuProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<Element | null>(null);
  const { colAxisLabelRef, rowAxisLabelRef } = useContext(InternalContext);
  const publishEvent = useSwimlaneEventPublisher();

  const onClose = () => {
    setAnchor(null);
  };

  const anchorOrigin: PopoverProps['anchorOrigin'] =
    level === 1
      ? { vertical: 'bottom', horizontal: 'center' }
      : { vertical: 'center', horizontal: 'right' };

  const openReorderMenu = () => {
    setAnchor(null); // close the menu
    const anchorEl = level === 1 ? colAxisLabelRef.current : rowAxisLabelRef.current;
    if (anchorEl) {
      publishEvent('openReorderHeadersMenu', { anchorEl, level, isGroupHeader, parentHeaderId });
    }
  };

  return (
    <>
      <div style={{ alignSelf: 'end', zIndex: 1000 }}>
        <Tooltip title={t(($) => $.views.visualization.header.options)}>
          <IconButton
            onClick={(e: React.MouseEvent) => setAnchor(e.currentTarget)}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '32px',
              height: '32px',
            }}
          >
            <span data-testid="swimlane-header-context-menu-button">
              <Icon className="size-6" />
            </span>
          </IconButton>
        </Tooltip>
      </div>
      <StyledContextMenu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        MenuListProps={{
          'aria-labelledby': 'header-menu',
        }}
        anchorOrigin={anchorOrigin}
      >
        <Default
          hideHeader={hideHeader}
          onOpenCard={onOpenCard}
          handleClose={onClose}
          openCardLabel={openCardLabel}
          hideHeaderTooltip={hideHeaderTooltip}
          reorderDisabledTooltip={reorderDisabledTooltip}
          isReOrderingDisabled={isReOrderingDisabled}
          isViewCardOptionDisabled={isViewCardOptionDisabled}
          openReorderMenu={openReorderMenu}
        />
      </StyledContextMenu>
    </>
  );
}
